import React from 'react';
import { fileUrl } from '@/lib/api';

/**
 * PresentingSponsorBadge
 * Shared visual primitive for the Presenting Sponsor (e.g. USA Today Sports
 * Media Group), surfaced in three placements on the homepage. Each placement
 * passes its own eyebrow label, theme (dark/light copy), size variant, and
 * optional placement-specific test id.
 *
 * Props:
 *   sponsor   — { enabled, logo, alt, url, name } subset of presenting_sponsor
 *   eyebrow   — eyebrow label string (caller decides which field of the
 *               sponsor doc to use — hero/footer pass hero_eyebrow_label;
 *               partners passes partners_eyebrow_label)
 *   theme     — 'dark' | 'light' (controls eyebrow + fallback name text color)
 *   size      — 'sm' | 'md' | 'lg' (logo max-height)
 *   testid    — root data-testid for the placement
 *   align     — 'start' | 'center' (flex alignment)
 *   showName  — when true, renders the sponsor display name below the logo
 *               as a visible caption (used by the Partners card placement).
 *
 * Renders nothing when sponsor.enabled is false OR a logo+name are both
 * missing. The wrapper anchor is omitted when no click-through URL exists.
 */
export default function PresentingSponsorBadge({
  sponsor,
  eyebrow,
  theme = 'dark',
  size = 'md',
  testid = 'presenting-sponsor',
  align = 'start',
  showName = false,
}) {
  if (!sponsor || sponsor.enabled === false) return null;
  const hasLogo = !!(sponsor.logo && String(sponsor.logo).trim() !== '');
  const hasName = !!(sponsor.name && String(sponsor.name).trim() !== '');
  if (!hasLogo && !hasName) return null;

  const eyebrowText = (eyebrow && String(eyebrow).trim()) || 'PRESENTED BY';
  const alt = sponsor.alt || sponsor.name || 'Presenting sponsor';
  const url = sponsor.url && sponsor.url.trim();

  const eyebrowColor =
    theme === 'light' ? 'text-ink-soft' : 'text-white/70';
  const nameColor =
    theme === 'light' ? 'text-ink-primary' : 'text-white';
  const logoHeight =
    size === 'lg' ? 'h-20 md:h-24'
    : size === 'sm' ? 'h-10 md:h-12'
    : 'h-12 md:h-14';
  const alignCls = align === 'center' ? 'items-center text-center' : 'items-start text-left';

  const logoEl = hasLogo ? (
    <img
      src={fileUrl(sponsor.logo)}
      alt={alt}
      className={`${logoHeight} w-auto object-contain shrink-0`}
      decoding="async"
      loading="lazy"
      data-testid={`${testid}-logo`}
    />
  ) : null;

  const nameEl = showName && hasName ? (
    <span
      className={`mt-2 font-display font-bold uppercase ${nameColor} text-[12px] md:text-[13px] tracking-wider2`}
      data-testid={`${testid}-name`}
    >
      {sponsor.name}
    </span>
  ) : null;

  const innerContent = (
    <>
      <span
        className={`block font-body font-semibold uppercase ${eyebrowColor} text-[10px] md:text-[11px] tracking-wider3`}
        data-testid={`${testid}-eyebrow`}
      >
        {eyebrowText}
      </span>
      <span className={`mt-1.5 flex flex-col ${alignCls}`}>
        {logoEl}
        {nameEl}
      </span>
    </>
  );

  const containerCls = `flex flex-col ${alignCls}`;

  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={alt}
        className={`${containerCls} group focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-transparent transition-opacity hover:opacity-90`}
        data-testid={testid}
      >
        {innerContent}
      </a>
    );
  }

  return (
    <div className={containerCls} data-testid={testid}>
      {innerContent}
    </div>
  );
}
